import React, { useEffect, useRef } from "react";
import {
  ArrowRight,
  Award,
  Check,
  ChevronRight,
  Droplets,
  Globe2,
  Leaf,
  Lightbulb,
  Play,
  Rocket,
  Sprout,
  Target,
  TrendingUp,
  Users,
  Wheat,
  Zap,
} from "lucide-react";

import CommercialGreenhouse from "../image/CommercialGreenhouse.png";
import Sustainability from "../image/Sustainability.png";
import Ourmission from "../image/ourmission.png";
import LettuceRaftHydroponics from "../image/LettuceRaftHydroponics.png";
const About = () => {
  const sectionRef = useRef(null);

  // ============================================================
  // SCROLL REVEAL ANIMATION
  // ============================================================
  useEffect(() => {
    const elements = document.querySelectorAll(".reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      {
        threshold: 0.12,
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  // ============================================================
  // DATA
  // ============================================================

  const stats = [
    {
      icon: Users,
      number: "500+",
      title: "Happy Clients",
    },
    {
      icon: Sprout,
      number: "1M+",
      title: "Plants Grown",
    },
    {
      icon: TrendingUp,
      number: "50+",
      title: "Projects Completed",
    },
    {
      icon: Globe2,
      number: "4+",
      title: "Years of Experience",
    },
  ];

  const values = [
    "Sustainability",
    "Innovation",
    "Transparency",
    "Quality",
    "Care for the Planet",
  ];

  const milestones = [
    {
      year: "2019",
      title: "The Beginning",
      description:
        "Founded with a vision for sustainable farming.",
      icon: Sprout,
    },
    {
      year: "2021",
      title: "First Project",
      description:
        "Completed our first commercial hydroponic installation.",
      icon: TrendingUp,
    },
    {
      year: "2023",
      title: "Growing Impact",
      description:
        "Reached 500+ clients and multiple successful projects.",
      icon: Users,
    },
    {
      year: "2024",
      title: "A Greener Future",
      description:
        "Continued to innovate and create a positive impact.",
      icon: Award,
    },
  ];

  return (
    <div
      ref={sectionRef}
      className="min-h-screen overflow-hidden bg-white text-[#10231c]"
    >
     

      <section className="relative overflow-hidden bg-[#f7fbf7]">
        <div className="mx-auto max-w-[1500px]">
          {/* Breadcrumb */}
          <div className="px-5 pt-7 lg:px-12">
            <p className="text-sm font-medium text-gray-500">
              Home
              <ChevronRight
                size={14}
                className="mx-1 inline-block"
              />
              <span className="font-semibold text-[#087f4f]">
                About Us
              </span>
            </p>
          </div>

          <div className="grid min-h-[500px] grid-cols-1 items-center lg:grid-cols-2">
            {/* LEFT CONTENT */}
            <div className="hero-content relative z-10 px-5 py-14 lg:px-12 lg:py-20">
              <div className="hero-badge mb-4 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                  <Leaf size={17} />
                </span>

                <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#16854f]">
                  About Polybionic Technologies
                </p>
              </div>

              <h1 className="hero-title max-w-[620px] text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-[58px]">
                Innovating Today
                <br />
                for a{" "}
                <span className="text-[#087f4f]">
                  Greener
                </span>
                <br />
                Tomorrow
              </h1>

              <p className="hero-description mt-6 max-w-[590px] text-base leading-7 text-gray-600 lg:text-lg">
                At Polybionic Technologies, we are committed to
                building sustainable, innovative and
                technology-driven farming solutions for a
                healthier planet and a better future.
              </p>

              <div className="hero-buttons mt-8 flex flex-wrap gap-4">
                <a
                  href="#journey"
                  className="group flex items-center gap-2 rounded-full bg-[#087f4f] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-900/10 transition hover:-translate-y-1 hover:bg-[#056a41]"
                >
                  Our Journey
                  <ArrowRight
                    size={17}
                    className="arrow-animation"
                  />
                </a>

                <button className="group flex items-center gap-2 rounded-full border border-gray-300 bg-white px-7 py-3.5 text-sm font-semibold text-gray-700 transition hover:-translate-y-1 hover:border-[#087f4f] hover:text-[#087f4f]">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#087f4f] text-white">
                    <Play size={12} fill="currentColor" />
                  </span>
                  Watch Our Story
                </button>
              </div>

              {/* FEATURES */}
              <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-4">
                {[
                  {
                    icon: Leaf,
                    title: "Sustainable",
                    sub: "Farming",
                  },
                  {
                    icon: Droplets,
                    title: "Resource",
                    sub: "Efficient",
                  },
                  {
                    icon: Zap,
                    title: "Technology",
                    sub: "Driven",
                  },
                  {
                    icon: Globe2,
                    title: "A Greener",
                    sub: "Planet",
                  },
                ].map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="hero-feature flex items-center gap-2"
                      style={{
                        animationDelay: `${0.8 + index * 0.12}s`,
                      }}
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                        <Icon size={19} />
                      </span>

                      <div>
                        <p className="text-xs font-bold text-gray-800">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.sub}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className="relative h-full min-h-[430px] overflow-hidden lg:min-h-[560px]">
              <img
                src={LettuceRaftHydroponics}
                alt="Hydroponic farming"
                className="hero-image absolute inset-0 h-full w-full object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-r from-[#f7fbf7] via-transparent to-transparent lg:w-1/3" />

              {/* FLOATING DECORATION */}
              <div className="floating-leaf absolute left-8 top-20 text-white/80">
                <Leaf size={42} strokeWidth={1.2} />
              </div>

              <div className="floating-leaf-delay absolute bottom-32 left-16 text-white/70">
                <Sprout size={34} strokeWidth={1.2} />
              </div>

              {/* QUOTE */}
              <div className="absolute right-6 top-20 z-10 lg:right-12">
                <div className="w-[320px] bg-gradient-to-r from-black/40 via-[#234a2d]/45 to-black/30 px-8 py-5 backdrop-blur-sm lg:w-[360px]">
                  <p
                    className="text-center text-2xl font-medium italic leading-tight text-white lg:text-3xl"
                    style={{ fontFamily: "Georgia, serif" }}
                  >
                    Good Food
                  </p>

                  <p
                    className="mt-1 text-center text-2xl font-medium italic leading-tight text-white lg:text-3xl"
                    style={{ fontFamily: "Georgia, serif" }}
                  >
                    Brighter Tomorrow
                  </p>
                </div>
              </div>

              {/* BOTTOM CARD */}
              <div className="absolute bottom-0 right-0 rounded-tl-[40px] bg-[#075b3a]/95 px-8 py-6 text-white">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                    <Leaf
                      size={28}
                      className="floating-leaf"
                    />
                  </span>

                  <div>
                    <p className="font-bold">
                      Nurturing Nature
                    </p>
                    <p className="font-bold">
                      with Innovation
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          WHO WE ARE
      ===================================================== */}

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* CONTENT */}
            <div className="">
              <div className="mb-3 flex items-center gap-2">
                <span className="h-[2px] w-8 bg-[#16854f]" />

                <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                  Who We Are
                </p>
              </div>

              <h2 className="text-3xl font-extrabold sm:text-4xl">
                Science. Sustainability.
                <br />
                Smarter Farming.
              </h2>

              <p className="mt-5 max-w-[620px] leading-7 text-gray-600">
                Polybionic Technologies is an innovation-driven
                company focused on modern hydroponic farming,
                smart agriculture and sustainable food
                production.
              </p>

              <p className="mt-4 max-w-[620px] leading-7 text-gray-600">
                We combine technology, research and practical
                solutions to make fresh, nutritious food
                accessible to everyone.
              </p>

              <p className="mt-4 max-w-[620px] leading-7 text-gray-600">
                Our goal is to empower farmers, businesses and
                communities with advanced farming systems that
                use less water, less space and no soil, while
                delivering higher yields and better quality
                produce.
              </p>

              <a
                href="#story"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-7 py-3.5 text-sm font-bold text-white transition hover:-translate-y-1 hover:bg-[#056a41]"
              >
                More About Us
                <ArrowRight
                  size={17}
                  className="arrow-animation"
                />
              </a>
            </div>

            {/* IMAGE */}
            <div className=" image-container relative overflow-hidden rounded-[28px] shadow-sm">
              <img
                src={Sustainability}
                alt="Modern agriculture"
                className="image-hover h-[350px] w-full object-cover lg:h-[390px]"
              />

              <div className="absolute bottom-5 left-5 flex items-center gap-3 rounded-2xl bg-white/95 px-5 py-4 shadow-xl backdrop-blur">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                  <Sprout size={23} />
                </div>

                <div>
                  <p className="text-sm font-bold text-gray-900">
                    Sustainable
                  </p>
                  <p className="text-xs text-gray-500">
                    Farming Future
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          STATS
      ===================================================== */}

      <section className="border-y border-green-100 bg-[#f1faf3]">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 lg:grid-cols-4">
          {stats.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={index}
                className="stat-card reveal flex items-center justify-center gap-4 border-green-100 px-5 py-7 lg:border-r last:border-r-0"
                style={{
                  transitionDelay: `${index * 100}ms`,
                }}
              >
                <span className="stat-icon flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-sm">
                  <Icon size={28} />
                </span>

                <div>
                  <p className="text-2xl font-extrabold text-gray-900">
                    {item.number}
                  </p>

                  <p className="text-sm text-gray-500">
                    {item.title}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* =====================================================
          OUR PURPOSE
      ===================================================== */}

      <section className="relative overflow-hidden bg-white py-14 sm:py-16 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="reveal mb-8">
            <div className="mb-2 flex items-center gap-2">
              <Leaf size={16} className="text-[#16854f]" />

              <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Our Purpose
              </p>
            </div>

            <h2 className="text-3xl font-extrabold leading-tight text-[#10231c] sm:text-4xl lg:text-[38px]">
              Our Vision, Mission and Values
            </h2>
          </div>

          <div className="grid gap-5 lg:grid-cols-[1fr_1fr_1fr_0.9fr]">
            {/* VISION */}
            <div
              className="purpose-card card-hover reveal rounded-2xl border border-transparent bg-[#f7f9f8] p-7 shadow-sm"
              style={{ transitionDelay: "100ms" }}
            >
              <div className="icon-hover relative z-10 mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                <Target size={28} />
              </div>

              <h3 className="relative z-10 text-xl font-bold text-[#10231c]">
                Our Vision
              </h3>

              <p className="relative z-10 mt-4 text-[15px] leading-7 text-gray-600">
                A world with safe, sustainable and fresh food
                for every community.
              </p>
            </div>

            {/* MISSION */}
            <div
              className="purpose-card card-hover reveal rounded-2xl border border-transparent bg-[#f7f9f8] p-7 shadow-sm"
              style={{ transitionDelay: "200ms" }}
            >
              <div className="icon-hover relative z-10 mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                <Rocket size={28} />
              </div>

              <h3 className="relative z-10 text-xl font-bold text-[#10231c]">
                Our Mission
              </h3>

              <p className="relative z-10 mt-4 text-[15px] leading-7 text-gray-600">
                To make modern farming simple, accessible and
                impactful through innovation and technology.
              </p>
            </div>

            {/* VALUES */}
            <div
              className="purpose-card card-hover reveal rounded-2xl border border-transparent bg-[#f7f9f8] p-7 shadow-sm"
              style={{ transitionDelay: "300ms" }}
            >
              <div className="icon-hover relative z-10 mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                <Lightbulb size={28} />
              </div>

              <h3 className="relative z-10 text-xl font-bold text-[#10231c]">
                Our Values
              </h3>

              <div className="relative z-10 mt-4 space-y-2.5">
                {values.map((value, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-sm text-gray-600"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#dcf8e7] text-[#16854f]">
                      <Check size={12} strokeWidth={3} />
                    </span>

                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* IMAGE */}
            <div
              className="reveal image-container relative min-h-[300px] overflow-hidden rounded-2xl lg:min-h-full"
              style={{ transitionDelay: "400ms" }}
            >
              <img
                src={Ourmission}
                alt="Small innovations big impact"
                className="image-hover absolute inset-0 h-full w-full object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-black/10" />

              <div className="absolute bottom-5 left-5 right-5 rounded-xl bg-black/30 p-4 text-white backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <Sprout size={20} />
                  <span className="text-sm font-semibold">
                    Small Innovations. Big Impact.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          OUR STORY
      ===================================================== */}

      <section
        id="story"
        className="bg-[#f7fbf7] py-16 lg:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* IMAGE */}
            <div className=" story-image relative overflow-hidden rounded-2xl shadow-lg">
              <img
                src={CommercialGreenhouse}
                alt="Hydroponic farm"
                className="image-hover h-[350px] w-full object-cover lg:h-[400px]"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />

              {/* PLAY BUTTON */}
              <button className="story-play absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-2xl">
                <Play size={27} fill="currentColor" />
              </button>

              <div className="absolute bottom-5 left-5 rounded-full bg-white/90 px-4 py-2 text-xs font-bold text-[#087f4f] backdrop-blur">
                <div className="flex items-center gap-2">
                  <Wheat size={15} />
                  Smart Agriculture
                </div>
              </div>
            </div>

            {/* STORY CONTENT */}
            <div className="">
              <div className="mb-2 flex items-center gap-2">
                <Leaf size={16} className="text-[#16854f]" />

                <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                  Our Story
                </p>
              </div>

              <h2 className="text-3xl font-extrabold sm:text-4xl">
                From an Idea to a
                <br />
                <span className="text-[#087f4f]">
                  Greener Future
                </span>
              </h2>

              <p className="mt-5 leading-7 text-gray-600">
                Polybionic Technologies was founded with a
                simple idea — to use technology to solve
                real-world problems in food production.
              </p>

              <p className="mt-4 leading-7 text-gray-600">
                What started as a vision has now grown into a
                mission to build advanced hydroponic farming
                solutions that help people grow more food with
                fewer resources.
              </p>

              <p className="mt-4 leading-7 text-gray-600">
                Today, we work with farmers, businesses,
                educational institutions and communities to
                create a sustainable and food-secure future.
              </p>

              <div className="mt-7 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <a
                  href="#journey"
                  className="group inline-flex w-fit items-center gap-2 rounded-full bg-[#087f4f] px-7 py-3.5 text-sm font-bold text-white transition hover:-translate-y-1 hover:bg-[#056a41]"
                >
                  Our Journey
                  <ArrowRight
                    size={17}
                    className="arrow-animation"
                  />
                </a>

                {/* QUOTE */}
                <div className="quote-card relative w-full max-w-[280px] overflow-hidden rounded-xl border border-green-100 bg-gradient-to-br from-[#f1fbf3] via-white to-[#edf9ef] px-5 py-5 shadow-sm">
                  <div className="absolute left-2 top-0 text-[48px] font-bold leading-none text-[#087f4f]">
                    “
                  </div>

                  <div className="absolute right-4 top-4 text-[#63bd4d]">
                    <Leaf size={24} />
                  </div>

                  <div className="relative z-10 pt-8">
                    <p className="pr-4 text-[12px] font-medium leading-[1.55] text-[#26352e]">
                      We believe technology and nature can grow
                      together for a brighter tomorrow.
                    </p>

                    <p className="mt-4 text-[9px] font-bold text-[#087f4f]">
                      — Team Polybionic
                    </p>
                  </div>

                  <div className="absolute -bottom-4 right-2 text-[#8ed477]">
                    <Sprout size={55} strokeWidth={1.2} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          JOURNEY / MILESTONES
      ===================================================== */}

      <section
        id="journey"
        className="bg-white py-16 lg:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="reveal mb-12">
            <div className="mb-2 flex items-center gap-2">
              <TrendingUp
                size={17}
                className="text-[#16854f]"
              />

              <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Our Journey
              </p>
            </div>

            <h2 className="text-3xl font-extrabold sm:text-4xl">
              Key Milestones
            </h2>

            <p className="mt-3 max-w-[600px] text-gray-500">
              From a simple idea to building sustainable
              farming solutions for a better future.
            </p>
          </div>

          {/* DESKTOP TIMELINE */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* LINE */}
              <div className="absolute left-0 right-0 top-7 h-[2px] bg-green-200" />

              <div className="relative grid grid-cols-4 gap-8">
                {milestones.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="timeline-item reveal"
                      style={{
                        transitionDelay: `${index * 150}ms`,
                      }}
                    >
                      <div className="timeline-dot relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-[#087f4f] text-white shadow-lg">
                        <Icon size={24} />
                      </div>

                      <p className="mt-5 text-sm font-bold text-[#087f4f]">
                        {item.year}
                      </p>

                      <h3 className="mt-1 text-lg font-bold">
                        {item.title}
                      </h3>

                      <p className="mt-2 max-w-[230px] text-sm leading-6 text-gray-500">
                        {item.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* MOBILE TIMELINE */}
          <div className="relative space-y-8 lg:hidden">
            <div className="absolute bottom-5 left-6 top-5 w-[2px] bg-green-200" />

            {milestones.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="timeline-item reveal relative flex gap-5"
                  style={{
                    transitionDelay: `${index * 120}ms`,
                  }}
                >
                  <div className="relative z-10 flex-shrink-0">
                    <div className="timeline-dot flex h-12 w-12 items-center justify-center rounded-full bg-[#087f4f] text-white shadow-md">
                      <Icon size={21} />
                    </div>
                  </div>

                  <div className="pb-2">
                    <p className="text-sm font-bold text-[#087f4f]">
                      {item.year}
                    </p>

                    <h3 className="mt-1 text-lg font-bold">
                      {item.title}
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-gray-500">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="relative overflow-hidden bg-[#034d32] py-14">
        {/* Animated Orbs */}
        <div className="pointer-events-none absolute inset-0 opacity-20">
          <div className="cta-orb absolute -right-20 -top-20 h-72 w-72 rounded-full bg-green-300 blur-3xl" />

          <div className="cta-orb-two absolute -bottom-20 left-20 h-72 w-72 rounded-full bg-green-200 blur-3xl" />
        </div>

        {/* Decorative Icons */}
        <div className="floating-leaf absolute left-[8%] top-8 text-white/10">
          <Leaf size={75} />
        </div>

        <div className="floating-leaf-delay absolute right-[10%] bottom-8 text-white/10">
          <Sprout size={65} />
        </div>

        <div className="relative mx-auto flex max-w-[1400px] flex-col justify-between gap-7 px-5 md:flex-row md:items-center lg:px-10">
          <div className="">
            <div className="mb-3 flex items-center gap-2 text-green-200">
              <Leaf size={18} />

              <span className="text-xs font-bold uppercase tracking-[0.18em]">
                Grow With Us
              </span>
            </div>

            <h2 className="text-2xl font-extrabold text-white sm:text-3xl">
              Let's Build a Greener Tomorrow Together
            </h2>

            <p className="mt-2 max-w-[650px] text-sm text-green-100 sm:text-base">
              Partner with us to bring innovative farming
              solutions to your community or business.
            </p>
          </div>

          <div className=" flex flex-wrap gap-3">
            <button className="cta-button group flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-[#087f4f]">
              Contact Us
              <ArrowRight
                size={17}
                className="arrow-animation"
              />
            </button>

            <button className="cta-button group flex items-center gap-2 rounded-full bg-[#087f4f] px-7 py-3.5 text-sm font-bold text-white ring-1 ring-white/20">
              Get a Quote
              <ArrowRight
                size={17}
                className="arrow-animation"
              />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;